import { createAbortableTransaction, isAbortError, storeRequestPromise, } from './idb.js';
import { Disposable } from './utils/Disposable.js';
export class IDBService extends Disposable {
    constructor(db, { log } = {}) {
        super();
        this.db = db;
        this.globalAbortController = new AbortController();
        this.createTransaction = (storeNames, opts) => {
            const tx = createAbortableTransaction(this.db, storeNames, (opts === null || opts === void 0 ? void 0 : opts.mode) || 'readonly', opts === null || opts === void 0 ? void 0 : opts.abort, this.log);
            this.globalAbortController.signal.addEventListener('abort', tx.abort);
            tx.addEventListener('complete', () => {
                this.globalAbortController.signal.removeEventListener('abort', tx.abort);
            });
            tx.addEventListener('error', () => {
                this.globalAbortController.signal.removeEventListener('abort', tx.abort);
            });
            return tx;
        };
        this.run = async (storeName, getRequest, opts) => {
            var _a;
            if (this.disposed || ((_a = opts === null || opts === void 0 ? void 0 : opts.transaction) === null || _a === void 0 ? void 0 : _a.error))
                return Promise.resolve(undefined);
            const tx = (opts === null || opts === void 0 ? void 0 : opts.transaction) || this.createTransaction([storeName], opts);
            const store = tx.objectStore(storeName);
            const request = getRequest(store);
            return storeRequestPromise(request);
        };
        this.runAll = async (storeName, getRequests, opts) => {
            var _a;
            if (this.disposed || ((_a = opts === null || opts === void 0 ? void 0 : opts.transaction) === null || _a === void 0 ? void 0 : _a.error))
                return Promise.resolve([]);
            const tx = (opts === null || opts === void 0 ? void 0 : opts.transaction) || this.createTransaction([storeName], opts);
            const store = tx.objectStore(storeName);
            const requests = getRequests(store);
            return Promise.all(requests.map(storeRequestPromise));
        };
        this.iterate = async (storeName, getRequest, iterator, opts) => {
            const tx = (opts === null || opts === void 0 ? void 0 : opts.transaction) || this.createTransaction([storeName], opts);
            const store = tx.objectStore(storeName);
            const request = getRequest(store);
            if (Array.isArray(request)) {
                return Promise.all(request.map((req) => {
                    return new Promise((resolve, reject) => {
                        req.onsuccess = () => {
                            const cursor = req.result;
                            if (cursor) {
                                iterator(cursor.value, store);
                                cursor.continue();
                            }
                            else {
                                resolve();
                            }
                        };
                        req.onerror = () => {
                            if (req.error && isAbortError(req.error)) {
                                resolve();
                            }
                            else {
                                reject(req.error);
                            }
                        };
                    });
                })).then(() => undefined);
            }
            return new Promise((resolve, reject) => {
                request.onsuccess = () => {
                    const cursor = request.result;
                    if (cursor) {
                        iterator(cursor.value, store);
                        cursor.continue();
                    }
                    else {
                        resolve();
                    }
                };
                request.onerror = () => {
                    if (request.error && isAbortError(request.error)) {
                        resolve();
                    }
                    else {
                        reject(request.error);
                    }
                };
            });
        };
        this.clear = (storeName) => {
            return this.run(storeName, (store) => store.clear(), {
                mode: 'readwrite',
            });
        };
        this.log = log;
        this.addDispose(() => {
            this.globalAbortController.abort();
        });
    }
}
//# sourceMappingURL=IDBService.js.map